/**
 * MTRS-2467 trace pieces shared by `proxy.ts` (built for CV and LCV) and the
 * campaign under `app/lcv/components/campaigns` (renamed away in the CV build),
 * so they live here, not in `app/lcv`. Browser-safe: no `server-only`.
 */

import logger from '@/lib/logger';

/** Prefix of every trace log line: search `BC2026_TRACE|` in Datadog. */
export const TRACE_PREFIX = 'BC2026_TRACE';

/** Step 00, logged by `proxy.ts`. */
export const PROXY_TRACE_STEP = { no: 0, name: '00_proxy' } as const;

/** On while `SPECIAL_SALE_CAMPAIGN_TRACE=1` (server env). */
export const isTraceEnabled = () =>
  process.env.SPECIAL_SALE_CAMPAIGN_TRACE === '1';

/** The query string without the lead id's value; capped. */
export const sanitizeSearch = (search: string): string => {
  const params = new URLSearchParams(search);
  if (params.has('leadId')) params.set('leadId', '<set>');
  return params.toString().slice(0, 300);
};

/** Every URL query param as `url_<key>` trace fields (lead id masked); at most 20. */
export const toTraceUrlParams = (search: string): Record<string, string> =>
  Object.fromEntries(
    [...new URLSearchParams(sanitizeSearch(search)).entries()]
      .slice(0, 20)
      .map(([key, value]) => [`url_${key}`.slice(0, 40), value.slice(0, 300)])
  );

/**
 * One trace line at **info** level (`console.log`), not `logger.service`
 * (`console.error`), so traces don't show as errors in Datadog:
 * `BC2026_TRACE|<flow>|<NN_step>|<outcome>|URL|<url>|STATUS|<status>|PARAMS|<json>`.
 */
export const writeTraceLine = ({
  flow,
  step,
  outcome,
  url,
  status,
  params,
}: {
  flow: string;
  step: string;
  outcome: string;
  url: string;
  status?: number;
  params: Record<string, unknown>;
}) => {
  logger.info(
    `${TRACE_PREFIX}|${flow}|${step}|${outcome}|URL|${url}|STATUS|${status ?? '-'}|PARAMS|${JSON.stringify(params)}`
  );
};
